import React, { useState, useCallback, useMemo } from 'react'
import {
	ChevronDown,
	ChevronUp,
	Dumbbell,
	Shirt,
	Watch,
	ShoppingBag,
	Calendar,
	MapPin,
	Palette,
	Ruler,
	DollarSign,
	X,
	Filter,
} from 'lucide-react'
import { useSelector } from 'react-redux'
import { RootState } from '@/store/store'
import { Entities } from '@/entities/api/entities'
import { AdvancedProductFilter } from '@/entities/api/products/product-filters'
import {
	colorsMap,
	flavorMap,
	modalitiesMap,
	objectivesMap,
	sizeMap,
} from '@/entities/api/products/product'
import { Link } from 'react-router-dom'
import { entityToSpanish } from '@/utils/entity-convert'

interface MenuSubCategoriesProps {
	currentType: Entities
	onFiltersChange: (filters: AdvancedProductFilter) => void
	currentFilters: AdvancedProductFilter
	selectedCategoryId?: string
}

const MenuSubCategories: React.FC<MenuSubCategoriesProps> = ({
	currentType,
	onFiltersChange,
	selectedCategoryId,
}) => {
	const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false)

	const [openSections, setOpenSections] = useState<{ [key: string]: boolean }>(
		() => ({
			price: false,
			color: false,
			size: false,
			flavor: false,
			objectives: false,
			modality: false,
			...(selectedCategoryId ? {} : { categories: true }),
		})
	)

	const [selectedSubcategories, setSelectedSubcategories] = useState<string[]>(
		selectedCategoryId ? [selectedCategoryId] : []
	)
	const [selectedPriceRange, setSelectedPriceRange] = useState<{
		min?: number
		max?: number
	}>({})
	const [selectedColors, setSelectedColors] = useState<string[]>([])
	const [selectedSizes, setSelectedSizes] = useState<string[]>([])
	const [selectedFlavors, setSelectedFlavors] = useState<string[]>([])
	const [selectedObjectives, setSelectedObjectives] = useState<string[]>([])
	const [selectedModalities, setSelectedModalities] = useState<string[]>([])

	const categories = useSelector(
		(state: RootState) => state.category.categories
	)

	const subcategoriesForCurrentType = useMemo(
		() => categories.filter((cat) => cat.type === currentType),
		[categories, currentType]
	)

	const shouldShowCategoriesFilter = useMemo(
		() => !selectedCategoryId && subcategoriesForCurrentType.length > 0,
		[selectedCategoryId, subcategoriesForCurrentType.length]
	)

	const getCategoryIcon = useCallback((type: Entities) => {
		switch (type) {
			case Entities.SUPPLEMENTS:
				return <Dumbbell className='w-4 h-4' />
			case Entities.CLOTHES:
				return <Shirt className='w-4 h-4' />
			case Entities.ACCESSORIES:
				return <Watch className='w-4 h-4' />
			case Entities.PLANS_PERSONALIZED:
				return <Calendar className='w-4 h-4' />
			default:
				return <ShoppingBag className='w-4 h-4' />
		}
	}, [])

	const applyFilters = useCallback(() => {
		const filters: AdvancedProductFilter = {
			categoryType: {
				type: currentType,
				...(selectedSubcategories.length > 0 && {
					categoryId: selectedSubcategories[0],
				}),
			},
			attributes: {},
			onlyActive: true,
			page: 1,
			limit: 12,
		}

		if (
			selectedPriceRange.min !== undefined ||
			selectedPriceRange.max !== undefined
		) {
			filters.priceRange = {
				minPrice: selectedPriceRange.min,
				maxPrice: selectedPriceRange.max,
				includeAttributePrices: true,
			}
		}

		if (selectedColors.length > 0) {
			filters.attributes!.colors = selectedColors
		}
		if (selectedSizes.length > 0) {
			filters.attributes!.sizes = selectedSizes
		}
		if (selectedFlavors.length > 0) {
			filters.attributes!.flavors = selectedFlavors
		}
		if (selectedObjectives.length > 0) {
			filters.attributes!.objectives = selectedObjectives
		}
		if (selectedModalities.length > 0) {
			filters.attributes!.modalities = selectedModalities
		}

		onFiltersChange(filters)
	}, [
		currentType,
		selectedSubcategories,
		selectedPriceRange,
		selectedColors,
		selectedSizes,
		selectedFlavors,
		selectedObjectives,
		selectedModalities,
		onFiltersChange,
	])

	const toggleSection = useCallback((sectionId: string) => {
		setOpenSections((prev) => ({
			...prev,
			[sectionId]: !prev[sectionId],
		}))
	}, [])

	const toggleSubcategory = useCallback(
		(categoryId: string) => {
			if (!selectedCategoryId) {
				setSelectedSubcategories((prev) =>
					prev.includes(categoryId)
						? prev.filter((id) => id !== categoryId)
						: [categoryId]
				)
			}
		},
		[selectedCategoryId]
	)

	const toggleColor = useCallback((color: string) => {
		setSelectedColors((prev) =>
			prev.includes(color) ? prev.filter((c) => c !== color) : [...prev, color]
		)
	}, [])

	const toggleSize = useCallback((size: string) => {
		setSelectedSizes((prev) =>
			prev.includes(size) ? prev.filter((s) => s !== size) : [...prev, size]
		)
	}, [])

	const toggleFlavor = useCallback((flavor: string) => {
		setSelectedFlavors((prev) =>
			prev.includes(flavor)
				? prev.filter((f) => f !== flavor)
				: [...prev, flavor]
		)
	}, [])

	const toggleObjective = useCallback((objective: string) => {
		setSelectedObjectives((prev) =>
			prev.includes(objective)
				? prev.filter((o) => o !== objective)
				: [...prev, objective]
		)
	}, [])

	const toggleModality = useCallback((modality: string) => {
		setSelectedModalities((prev) =>
			prev.includes(modality)
				? prev.filter((m) => m !== modality)
				: [...prev, modality]
		)
	}, [])

	const handlePriceRangeChange = useCallback((range: string) => {
		switch (range) {
			case '0-25.000 COP':
				setSelectedPriceRange({ min: 0, max: 25000 })
				break
			case '25.000-50.000 COP':
				setSelectedPriceRange({ min: 25000, max: 50000 })
				break
			case '50.000-100.000 COP':
				setSelectedPriceRange({ min: 50000, max: 100000 })
				break
			case '100.000-200.000 COP':
				setSelectedPriceRange({ min: 100000, max: 200000 })
				break
			case '200.000-500.000 COP':
				setSelectedPriceRange({ min: 200000, max: 500000 })
				break
			case '500.000-1.000.000 COP':
				setSelectedPriceRange({ min: 500000, max: 1000000 })
				break
			case '1.000.000+ COP':
				setSelectedPriceRange({ min: 1000000, max: 10000000 })
				break
			default:
				setSelectedPriceRange({})
		}
	}, [])

	const clearAllFilters = useCallback(() => {
		setSelectedSubcategories(selectedCategoryId ? [selectedCategoryId] : [])
		setSelectedPriceRange({})
		setSelectedColors([])
		setSelectedSizes([])
		setSelectedFlavors([])
		setSelectedObjectives([])
		setSelectedModalities([])

		onFiltersChange({
			categoryType: {
				type: currentType,
				...(selectedCategoryId && { categoryId: selectedCategoryId }),
			},
			onlyActive: true,
			page: 1,
			limit: 12,
		})
	}, [selectedCategoryId, currentType, onFiltersChange])

	const toggleMobileMenu = useCallback((): void => {
		setIsMenuOpen(!isMenuOpen)
	}, [isMenuOpen])

	const priceRanges = useMemo(
		() => [
			{ id: '0-25.000 COP', label: '0 - 25.000 COP', min: 0, max: 25000 },
			{
				id: '25.000-50.000 COP',
				label: '25.000 - 50.000 COP',
				min: 25000,
				max: 50000,
			},
			{
				id: '50.000-100.000 COP',
				label: '50.000 - 100.000 COP',
				min: 50000,
				max: 100000,
			},
			{
				id: '100.000-200.000 COP',
				label: '100.000 - 200.000 COP',
				min: 100000,
				max: 200000,
			},
			{
				id: '200.000-500.000 COP',
				label: '200.000 - 500.000 COP',
				min: 200000,
				max: 500000,
			},
			{
				id: '500.000-1.000.000 COP',
				label: '500.000 - 1.000.000 COP',
				min: 500000,
				max: 1000000,
			},
			{
				id: '1.000.000+ COP',
				label: '1.000.000+ COP',
				min: 1000000,
				max: 10000000,
			},
		],
		[]
	)

	const shouldShowColors = useMemo(
		() =>
			currentType === Entities.CLOTHES || currentType === Entities.ACCESSORIES,
		[currentType]
	)
	const shouldShowSizes = useMemo(
		() =>
			currentType === Entities.CLOTHES || currentType === Entities.ACCESSORIES,
		[currentType]
	)
	const shouldShowFlavors = useMemo(
		() => currentType === Entities.SUPPLEMENTS,
		[currentType]
	)
	const shouldShowObjectives = useMemo(
		() => currentType === Entities.PLANS_PERSONALIZED,
		[currentType]
	)
	const shouldShowModalities = useMemo(
		() => currentType === Entities.PLANS_PERSONALIZED,
		[currentType]
	)

	const FilterSection: React.FC<{
		id: string
		title: string
		icon: React.ReactNode
		children: React.ReactNode
	}> = useCallback(
		({ id, title, icon, children }) => (
			<div className='border border-gray-200 rounded-lg mb-2 overflow-hidden bg-white shadow-sm hover:shadow-md transition-all duration-200'>
				<button
					onClick={() => toggleSection(id)}
					className='w-full flex items-center justify-between p-3 hover:bg-gradient-to-r hover:from-red-50 hover:to-pink-50 transition-all duration-200 group bg-white'
				>
					<div className='flex items-center space-x-2.5'>
						<div className='text-red-600 group-hover:text-red-700 transition-colors duration-200 p-1.5 rounded-lg bg-red-50 group-hover:bg-red-100'>
							{icon}
						</div>
						<span className='font-bold text-gray-900 text-sm group-hover:text-red-700 transition-colors duration-200'>
							{title}
						</span>
					</div>
					<div className='flex items-center space-x-2'>
						{openSections[id] ? (
							<ChevronUp className='w-4 h-4 text-gray-400 group-hover:text-red-500 transition-colors duration-200' />
						) : (
							<ChevronDown className='w-4 h-4 text-gray-400 group-hover:text-red-500 transition-colors duration-200' />
						)}
					</div>
				</button>
				{openSections[id] && (
					<div className='px-3 pb-3 space-y-1.5 bg-gradient-to-b from-gray-50 to-white border-t border-gray-100 max-h-[20rem] overflow-y-auto'>
						{children}
					</div>
				)}
			</div>
		),
		[openSections, toggleSection]
	)

	const hasActiveFilters = useMemo(
		() =>
			(!selectedCategoryId && selectedSubcategories.length > 0) ||
			Object.keys(selectedPriceRange).length > 0 ||
			selectedColors.length > 0 ||
			selectedSizes.length > 0 ||
			selectedFlavors.length > 0 ||
			selectedObjectives.length > 0 ||
			selectedModalities.length > 0,
		[
			selectedCategoryId,
			selectedSubcategories.length,
			selectedPriceRange,
			selectedColors.length,
			selectedSizes.length,
			selectedFlavors.length,
			selectedObjectives.length,
			selectedModalities.length,
		]
	)

	const totalActiveFilters = useMemo(
		() =>
			(!selectedCategoryId ? selectedSubcategories.length : 0) +
			(Object.keys(selectedPriceRange).length > 0 ? 1 : 0) +
			selectedColors.length +
			selectedSizes.length +
			selectedFlavors.length +
			selectedObjectives.length +
			selectedModalities.length,
		[
			selectedCategoryId,
			selectedSubcategories.length,
			selectedPriceRange,
			selectedColors.length,
			selectedSizes.length,
			selectedFlavors.length,
			selectedObjectives.length,
			selectedModalities.length,
		]
	)

	return (
		<>
			<button
				onClick={toggleMobileMenu}
				className={`lg:hidden fixed top-20 left-4 z-[9999] bg-white bg-opacity-95 backdrop-blur-xl text-gray-800 p-3.5 rounded-2xl shadow-2xl border border-gray-200 hover:bg-opacity-100 hover:shadow-red-500/25 hover:border-red-300 transition-all duration-300 transform hover:scale-110 active:scale-95 ${
					isMenuOpen
						? 'opacity-0 invisible pointer-events-none'
						: 'opacity-100 visible'
				}`}
				aria-label='Abrir filtros'
				style={{
					position: 'fixed',
					willChange: 'transform, opacity',
				}}
			>
				<div className='relative'>
					<Filter className='w-6 h-6 text-gray-700' />
					{totalActiveFilters > 0 && (
						<div className='absolute -top-2 -right-2 w-5 h-5 bg-gradient-to-r from-red-500 to-red-600 rounded-full border-2 border-white flex items-center justify-center shadow-lg'>
							<span className='text-white text-xs font-bold'>
								{totalActiveFilters > 9 ? '9+' : totalActiveFilters}
							</span>
						</div>
					)}
				</div>
			</button>

			{isMenuOpen && (
				<div
					className='lg:hidden fixed inset-0 bg-black bg-opacity-60 z-[9990] backdrop-blur-sm transition-all duration-300'
					onClick={toggleMobileMenu}
				/>
			)}

			<div
				className={`
					fixed lg:relative top-0 left-0 h-full lg:h-auto w-80 lg:w-72 xl:w-80 
					bg-gradient-to-b from-gray-50 to-white lg:bg-gradient-to-b lg:bg-white 
					rounded-none lg:rounded-xl shadow-2xl lg:shadow-none 
					lg:mr-4 xl:mr-6 overflow-hidden border-r lg:border-none border-gray-200 
					z-[9995] lg:z-auto transform transition-transform duration-300 ease-in-out lg:transform-none
					flex flex-col lg:block
					${isMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
				`}
			>
				<div className='relative bg-gradient-to-r from-red-600 via-red-600 to-red-700 text-white flex-shrink-0 lg:block'>
					<div className='absolute inset-0 bg-black bg-opacity-10'></div>
					<div className='relative p-3 lg:p-4'>
						<button
							onClick={toggleMobileMenu}
							className='lg:hidden absolute top-3 right-3 text-white hover:bg-white hover:bg-opacity-20 p-1.5 rounded-lg transition-all duration-200 z-10'
							aria-label='Cerrar filtros'
						>
							<X className='w-5 h-5' />
						</button>

						<div className='flex items-center justify-between pr-10 lg:pr-0'>
							<div className='flex items-center space-x-2 lg:space-x-3'>
								<div className='p-1.5 rounded-lg bg-white bg-opacity-20 backdrop-blur-sm'>
									<ShoppingBag className='w-5 h-5' />
								</div>
								<div>
									<h2 className='text-base lg:text-lg font-black tracking-tight'>
										Filtros
									</h2>
									{totalActiveFilters > 0 && (
										<span className='bg-white bg-opacity-25 text-white text-xs px-2 py-0.5 rounded-full font-bold'>
											{totalActiveFilters}
										</span>
									)}
								</div>
							</div>
							{hasActiveFilters && (
								<button
									onClick={clearAllFilters}
									className='text-xs hover:underline opacity-90 hover:opacity-100 transition-all font-medium bg-white bg-opacity-20 px-2 py-1 rounded-md backdrop-blur-sm hover:bg-opacity-30'
								>
									Limpiar
								</button>
							)}
						</div>
					</div>
				</div>

				<div
					className='overflow-y-auto flex-1 p-3 space-y-2 lg:overflow-visible lg:flex-none lg:p-3 lg:space-y-2'
					style={{
						scrollBehavior: 'smooth',
						overscrollBehavior: 'contain',
					}}
				>
					{shouldShowCategoriesFilter && (
						<FilterSection
							id='categories'
							title='Subcategorías'
							icon={getCategoryIcon(currentType)}
						>
							{subcategoriesForCurrentType.map((subcategory) => (
								<label
									key={subcategory._id}
									className='flex items-center space-x-2.5 p-2 hover:bg-white rounded-lg cursor-pointer transition-all duration-200 group border border-transparent hover:border-red-100 hover:shadow-sm'
								>
									<input
										type='radio'
										name='subcategory'
										checked={selectedSubcategories.includes(subcategory._id!)}
										onChange={() => toggleSubcategory(subcategory._id!)}
										className='w-3.5 h-3.5 text-red-600 border-gray-300 rounded focus:ring-red-500 focus:ring-2 transition-all duration-200'
									/>
									<span className='text-gray-900 text-sm group-hover:text-red-700 transition-colors duration-200 truncate font-medium'>
										{subcategory.name}
									</span>
								</label>
							))}
						</FilterSection>
					)}

					<FilterSection
						id='price'
						title='Precio'
						icon={<DollarSign className='w-4 h-4' />}
					>
						{priceRanges.map((range) => (
							<label
								key={range.id}
								className='flex items-center space-x-2.5 p-2 hover:bg-white rounded-lg cursor-pointer transition-all duration-200 group border border-transparent hover:border-green-100 hover:shadow-sm'
							>
								<input
									type='radio'
									name='priceRange'
									checked={
										(range.id === '0-25.000 COP' &&
											selectedPriceRange.min === 0 &&
											selectedPriceRange.max === 25000) ||
										(range.id === '25.000-50.000 COP' &&
											selectedPriceRange.min === 25000 &&
											selectedPriceRange.max === 50000) ||
										(range.id === '50.000-100.000 COP' &&
											selectedPriceRange.min === 50000 &&
											selectedPriceRange.max === 100000) ||
										(range.id === '100.000-200.000 COP' &&
											selectedPriceRange.min === 100000 &&
											selectedPriceRange.max === 200000) ||
										(range.id === '200.000-500.000 COP' &&
											selectedPriceRange.min === 200000 &&
											selectedPriceRange.max === 500000) ||
										(range.id === '500.000-1.000.000 COP' &&
											selectedPriceRange.min === 500000 &&
											selectedPriceRange.max === 1000000) ||
										(range.id === '1.000.000+ COP' &&
											selectedPriceRange.min === 1000000 &&
											selectedPriceRange.max === 10000000)
									}
									onChange={() => handlePriceRangeChange(range.id)}
									className='w-3.5 h-3.5 text-red-600 border-gray-300 rounded focus:ring-red-500 focus:ring-2'
								/>
								<span className='text-gray-900 flex-1 text-sm group-hover:text-red-700 transition-colors duration-200 font-medium'>
									{range.label}
								</span>
							</label>
						))}
					</FilterSection>

					{shouldShowColors && (
						<FilterSection
							id='color'
							title='Color'
							icon={<Palette className='w-4 h-4' />}
						>
							<div className='grid grid-cols-1 gap-1.5'>
								{Object.entries(colorsMap).map(([colorName, colorValue]) => (
									<label
										key={colorName}
										className='flex items-center space-x-2.5 p-2 hover:bg-white rounded-lg cursor-pointer transition-all duration-200 group border border-transparent hover:border-purple-100 hover:shadow-sm'
									>
										<input
											type='checkbox'
											checked={selectedColors.includes(colorName)}
											onChange={() => toggleColor(colorName)}
											className='w-3.5 h-3.5 text-red-600 border-gray-300 rounded focus:ring-red-500 focus:ring-2'
										/>
										<div
											className='w-5 h-5 rounded-full shadow-md border-2 border-white ring-1 ring-gray-200 flex-shrink-0'
											style={{ backgroundColor: colorValue }}
										></div>
										<span className='text-sm text-gray-900 group-hover:text-red-700 transition-colors duration-200 truncate font-medium'>
											{colorName}
										</span>
									</label>
								))}
							</div>
						</FilterSection>
					)}

					{shouldShowSizes && (
						<FilterSection
							id='size'
							title='Talla'
							icon={<Ruler className='w-4 h-4' />}
						>
							<div className='grid grid-cols-3 gap-1.5'>
								{Object.keys(sizeMap).map((size) => (
									<label
										key={size}
										className={`flex items-center justify-center p-2.5 border-2 rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md font-semibold ${
											selectedSizes.includes(size)
												? 'border-red-500 bg-gradient-to-r from-red-50 to-pink-50 text-red-700 shadow-md'
												: 'border-gray-300 hover:border-red-300 bg-white text-gray-700 hover:bg-red-50'
										}`}
									>
										<input
											type='checkbox'
											checked={selectedSizes.includes(size)}
											onChange={() => toggleSize(size)}
											className='sr-only'
										/>
										<span className='text-sm font-bold'>{size}</span>
									</label>
								))}
							</div>
						</FilterSection>
					)}

					{shouldShowFlavors && (
						<FilterSection
							id='flavor'
							title='Sabor'
							icon={
								<div className='w-4 h-4 rounded-full bg-gradient-to-r from-red-400 via-pink-400 to-purple-400'></div>
							}
						>
							{Object.keys(flavorMap).map((flavor) => (
								<label
									key={flavor}
									className='flex items-center space-x-2.5 p-2 hover:bg-white rounded-lg cursor-pointer transition-all duration-200 group border border-transparent hover:border-pink-100 hover:shadow-sm'
								>
									<input
										type='checkbox'
										checked={selectedFlavors.includes(flavor)}
										onChange={() => toggleFlavor(flavor)}
										className='w-3.5 h-3.5 text-red-600 border-gray-300 rounded focus:ring-red-500 focus:ring-2'
									/>
									<span className='text-gray-900 text-sm group-hover:text-red-700 transition-colors duration-200 truncate font-medium'>
										{flavor}
									</span>
								</label>
							))}
						</FilterSection>
					)}

					{shouldShowObjectives && (
						<FilterSection
							id='objectives'
							title='Objetivos'
							icon={<Dumbbell className='w-4 h-4' />}
						>
							{Object.entries(objectivesMap).map(([objective, description]) => (
								<label
									key={objective}
									className='flex items-start space-x-2.5 p-2 hover:bg-white rounded-lg cursor-pointer transition-all duration-200 group border border-transparent hover:border-blue-100 hover:shadow-sm'
								>
									<input
										type='checkbox'
										checked={selectedObjectives.includes(objective)}
										onChange={() => toggleObjective(objective)}
										className='w-3.5 h-3.5 text-red-600 border-gray-300 rounded focus:ring-red-500 focus:ring-2 mt-0.5'
									/>
									<div className='flex-1'>
										<span className='text-gray-900 text-sm group-hover:text-red-700 transition-colors duration-200 font-medium block'>
											{objective}
										</span>
										<span className='text-xs text-gray-500 mt-0.5 block'>
											{description}
										</span>
									</div>
								</label>
							))}
						</FilterSection>
					)}

					{shouldShowModalities && (
						<FilterSection
							id='modality'
							title='Modalidad'
							icon={<MapPin className='w-4 h-4' />}
						>
							{Object.entries(modalitiesMap).map(([modality, description]) => (
								<label
									key={modality}
									className='flex items-start space-x-2.5 p-2 hover:bg-white rounded-lg cursor-pointer transition-all duration-200 group border border-transparent hover:border-indigo-100 hover:shadow-sm'
								>
									<input
										type='checkbox'
										checked={selectedModalities.includes(modality)}
										onChange={() => toggleModality(modality)}
										className='w-3.5 h-3.5 text-red-600 border-gray-300 rounded focus:ring-red-500 focus:ring-2 mt-0.5'
									/>
									<div className='flex-1'>
										<span className='text-gray-900 text-sm group-hover:text-red-700 transition-colors duration-200 font-medium block'>
											{modality}
										</span>
										<span className='text-xs text-gray-500 mt-0.5 block'>
											{description}
										</span>
									</div>
								</label>
							))}
						</FilterSection>
					)}

					<div className='hidden lg:block mt-6 space-y-4'>
						<button
							onClick={applyFilters}
							className='w-full bg-gradient-to-r from-red-600 via-red-600 to-red-700 hover:from-red-700 hover:via-red-700 hover:to-red-800 text-white font-bold py-3 px-4 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl text-sm relative overflow-hidden group'
						>
							<div className='absolute inset-0 bg-white bg-opacity-20 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left'></div>
							<div className='relative flex items-center justify-center space-x-2'>
								<span>Aplicar Filtros</span>
								{totalActiveFilters > 0 && (
									<span className='bg-white bg-opacity-30 px-2 py-0.5 rounded-full text-xs font-bold backdrop-blur-sm'>
										({totalActiveFilters})
									</span>
								)}
							</div>
						</button>

						{subcategoriesForCurrentType.length > 0 && (
							<div className='bg-gradient-to-br mt-4 from-gray-50 to-white border border-gray-200 rounded-xl p-4 shadow-sm'>
								<div className='flex items-center space-x-2 mb-3'>
									<div className='p-1.5 rounded-lg bg-gradient-to-r from-red-50 to-pink-50 text-red-600'>
										{getCategoryIcon(currentType)}
									</div>
									<h3 className='font-bold text-sm text-gray-900'>
										Explorar por categoría
									</h3>
								</div>

								<div className='grid grid-cols-1 gap-1.5'>
									{subcategoriesForCurrentType.map((category) => (
										<Link
											key={category._id}
											to={`/SubCategoria/${entityToSpanish(
												category.type
											)}/${category.name.replace(/ /g, '-')}`}
											className='group flex items-center space-x-2.5 p-2.5 rounded-lg border border-transparent hover:border-red-200 hover:bg-gradient-to-r hover:from-red-50 hover:to-pink-50 transition-all duration-200 hover:shadow-sm'
										>
											<div className='w-2 h-2 rounded-full bg-gradient-to-r from-red-400 to-pink-400 opacity-60 group-hover:opacity-100 transition-opacity duration-200'></div>
											<span className='text-sm text-gray-700 group-hover:text-red-700 font-medium transition-colors duration-200 truncate'>
												{category.name}
											</span>
										</Link>
									))}
								</div>
							</div>
						)}
					</div>
				</div>

				<div className='lg:hidden p-3 bg-gradient-to-r from-gray-50 to-white border-t border-gray-200 flex-shrink-0'>
					<button
						onClick={() => {
							applyFilters()
							setIsMenuOpen(false)
						}}
						className='w-full bg-gradient-to-r from-red-600 via-red-600 to-red-700 hover:from-red-700 hover:via-red-700 hover:to-red-800 text-white font-bold py-3 px-4 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl text-sm relative overflow-hidden group'
					>
						<div className='absolute inset-0 bg-white bg-opacity-20 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left'></div>
						<div className='relative flex items-center justify-center space-x-2'>
							<span>Aplicar Filtros</span>
							{totalActiveFilters > 0 && (
								<span className='bg-white bg-opacity-30 px-2 py-0.5 rounded-full text-xs font-bold backdrop-blur-sm'>
									({totalActiveFilters})
								</span>
							)}
						</div>
					</button>
				</div>
			</div>
		</>
	)
}

export default MenuSubCategories
